import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ParcelOrderService from "../../services/parcel-order.service";
import { useSelectedBusiness } from "../../context/BusinessContext";
import {
  HiCheckCircle,
  HiTruck,
  HiCheck,
  HiClock,
  HiXCircle,
  HiClipboardDocument,
  HiExclamationTriangle,
  HiQuestionMarkCircle,
  HiOutlineCube,
  HiMagnifyingGlass,
  HiXMark,
  HiPlus,
  HiArrowRight,
  HiPrinter,
} from "react-icons/hi2";
import { MdFlightTakeoff, MdFlightLand } from "react-icons/md";
import { HiChevronDown, HiChevronUp } from "react-icons/hi2";

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_MAP = {
  delivered:  { label: "Delivered",  cls: "bg-green-100 text-green-700",   Icon: HiCheckCircle },
  completed:  { label: "Delivered",  cls: "bg-green-100 text-green-700",   Icon: HiCheckCircle },
  enroute: { label: "In Transit", cls: "bg-cyan-100 text-cyan-700",     Icon: HiTruck },
  ready_for_collection: { label: "Ready for Collection", cls: "bg-teal-100 text-teal-700", Icon: HiCheck },
  collected:  { label: "Collected",  cls: "bg-blue-100 text-blue-700",     Icon: HiCheck },
  pending:    { label: "Pending",    cls: "bg-amber-100 text-amber-700",   Icon: HiClock },
  cancelled:  { label: "Cancelled",  cls: "bg-red-100 text-red-600",       Icon: HiXCircle },
  created:    { label: "Created",    cls: "bg-gray-100 text-gray-600",     Icon: HiClipboardDocument },
  abandoned:  { label: "Abandoned",  cls: "bg-orange-100 text-orange-700", Icon: HiExclamationTriangle },
};

// Use delivery_status for tracking, fall back to order status
function orderStatus(o) {
  return o.delivery_status ?? "pending";
}

const TABS = [
  { key: "all",       label: "All" },
  { key: "active",    label: "Active",    match: (s) => ["enroute", "collected", "ready_for_collection"].includes(s) },
  { key: "pending",   label: "Pending",   match: (s) => s === "pending" },
  { key: "delivered", label: "Delivered", match: (s) => s === "delivered" },
  { key: "cancelled", label: "Cancelled", match: (s) => s === "cancelled" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return isNaN(d) ? dateStr : d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] ?? { label: status ?? "Unknown", cls: "bg-gray-100 text-gray-600", Icon: HiQuestionMarkCircle };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.cls}`}>
      <Icon className="h-3.5 w-3.5 flex-shrink-0" />
      {cfg.label}
    </span>
  );
}

// ─── Plane icons ─────────────────────────────────────────────────────────────

function TakeoffIcon() {
  return <MdFlightTakeoff className="h-4 w-4 flex-shrink-0 text-violet-400" />;
}

function LandingIcon() {
  return <MdFlightLand className="h-4 w-4 flex-shrink-0 text-gray-400" />;
}

// ─── Order table section ──────────────────────────────────────────────────────

// ─── Waybills expansion ──────────────────────────────────────────────────────

const waybillStatusColors = {
  pending: "bg-amber-100 text-amber-700",
  collected: "bg-blue-100 text-blue-700",
  delivered: "bg-green-100 text-green-700",
};

function WaybillsButton({ count, open, onClick }) {
  if (count <= 1) return null;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 px-2.5 py-1.5 rounded-lg transition-colors"
    >
      {open ? <HiChevronUp className="h-3.5 w-3.5" /> : <HiChevronDown className="h-3.5 w-3.5" />}
      {count} Waybills
    </button>
  );
}

function WaybillsList({ waybills }) {
  return (
    <div className="bg-violet-50/50 rounded-xl border border-violet-100 divide-y divide-violet-100 overflow-hidden">
      {waybills.map((w, i) => (
        <div key={w.id || i} className="flex items-center justify-between px-3 py-2 text-xs">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="font-mono font-semibold text-violet-600 flex-shrink-0">{w.sub_reference}</span>
            <span className="text-gray-700 truncate">{w.receiver_full_name || '—'}</span>
            <span className="text-gray-400 truncate hidden sm:inline">{w.receiver_cellphone || ''}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {w.collection_invoice_number && (
              <span className="text-gray-400 hidden sm:inline">#{w.collection_invoice_number}</span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${waybillStatusColors[w.status] || "bg-gray-100 text-gray-500"}`}>
              {w.status || "pending"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function WaybillsToggle({ order }) {
  const [open, setOpen] = useState(false);
  const waybills = order.waybills || [];
  if (waybills.length <= 1) return null;

  return (
    <div className="mt-2">
      <WaybillsButton count={waybills.length} open={open} onClick={() => setOpen(!open)} />
      {open && <div className="mt-2"><WaybillsList waybills={waybills} /></div>}
    </div>
  );
}

function DesktopOrderRow({ order, ref_, fromLabel, toLabel, displayStatus, navigate, waybills, hasMultipleWaybills }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr className="hover:bg-gray-50/60 transition-colors">
        <td className="px-5 py-4 align-middle whitespace-nowrap">
          <span className="font-mono text-sm font-semibold text-gray-800">{ref_}</span>
          {hasMultipleWaybills && (
            <span className="ml-2 text-[10px] font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-full">
              {waybills.length} waybills
            </span>
          )}
        </td>
        <td className="px-5 py-4 align-middle">
          <div className="flex flex-col gap-1">
            <div className="flex items-start gap-1.5">
              <TakeoffIcon />
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-gray-700 font-medium truncate max-w-[160px]">{fromLabel}</span>
                {order.pickup_location && (
                  <span className="text-xs text-gray-400 truncate max-w-[160px]">{order.pickup_location}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <LandingIcon />
              <span className="text-sm text-gray-500 truncate max-w-[160px]">{toLabel}</span>
            </div>
          </div>
        </td>
        <td className="px-5 py-4 align-middle">
          {order.parcel ? (
            <>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold">
                <HiOutlineCube className="h-3 w-3 flex-shrink-0" />
                {order.parcel.package_type}
              </span>
              <span className="block text-xs text-gray-400 mt-1">
                {[order.parcel.max_width, order.parcel.max_height, order.parcel.max_weight].filter(Boolean).join(" · ")}
              </span>
            </>
          ) : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-5 py-4 align-middle whitespace-nowrap">
          <span className="text-sm text-gray-400">{fmt(order.collection_date)}</span>
        </td>
        <td className="px-5 py-4 align-middle whitespace-nowrap">
          <StatusBadge status={displayStatus(order)} />
        </td>
        <td className="px-5 py-4 align-middle text-right whitespace-nowrap">
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => navigate(`/business/dashboard/track?ref=${order.reference_number ?? order.id}`)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 px-3 py-1.5 rounded-lg transition-colors"
            >
              Track
              <HiArrowRight className="h-3.5 w-3.5" />
            </button>
            <a
              href={`${process.env.REACT_APP_API_URL}parcel_orders/${order.id}/waybill`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg transition-colors"
            >
              <HiPrinter className="h-3.5 w-3.5" />
              Waybill
            </a>
            {hasMultipleWaybills && (
              <WaybillsButton count={waybills.length} open={open} onClick={() => setOpen(!open)} />
            )}
          </div>
        </td>
      </tr>
      {open && hasMultipleWaybills && (
        <tr>
          <td colSpan={6} className="px-5 py-3 bg-gray-50/40">
            <WaybillsList waybills={waybills} />
          </td>
        </tr>
      )}
    </>
  );
}

function OrderSection({ title, orders, total, navigate, accent, isPast }) {
  if (orders.length === 0) return null;

  const isUpcoming = accent === "violet";

  function displayStatus(order) {
    const s = orderStatus(order);
    if (isPast && s === "pending") return "abandoned";
    return s;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`h-2 w-2 rounded-full flex-shrink-0 ${isUpcoming ? "bg-violet-500" : "bg-gray-300"}`} />
        <h2 className="text-sm font-bold text-gray-700">{title}</h2>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isUpcoming ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-500"}`}>
          {orders.length}{total !== orders.length ? ` of ${total}` : ""}
        </span>
      </div>

      {/* ── Mobile cards ── */}
      <div className="md:hidden bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-50">
        {orders.map((order, i) => {
          const ref       = order.reference_number ?? order.id ?? `ORD-${i + 1}`;
          const fromTown  = order.departure_town?.name;
          const fromProv  = order.departure_province?.name;
          const toTown    = order.destination_town?.name;
          const toProv    = order.destination_province?.name;
          const fromLabel = [fromTown, fromProv].filter(Boolean).join(", ") || "—";
          const toLabel   = [toTown, toProv].filter(Boolean).join(", ") || "—";

          return (
            <div key={order.id ?? i} className="p-4 flex flex-col gap-3">
              {/* Reference */}
              <span className="font-mono text-sm font-semibold text-gray-800">{ref}</span>

              {/* Route */}
              <div className="flex flex-col gap-1">
                <div className="flex items-start gap-1.5">
                  <TakeoffIcon />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm text-gray-700 font-medium truncate">{fromLabel}</span>
                    {order.pickup_location && (
                      <span className="text-xs text-gray-400 truncate">{order.pickup_location}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <LandingIcon />
                  <span className="text-sm text-gray-500 truncate">{toLabel}</span>
                </div>
              </div>

              <div className="flex gap-2">
                {/* Track button */}
                <button
                  onClick={() => navigate(`/business/dashboard/track?ref=${order.reference_number ?? order.id}`)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 px-3 py-2 rounded-lg transition-colors"
                >
                  Track
                  <HiArrowRight className="h-3.5 w-3.5" />
                </button>
                {/* Print waybill */}
                <a
                  href={`${process.env.REACT_APP_API_URL}parcel_orders/${order.id}/waybill`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 px-3 py-2 rounded-lg transition-colors"
                >
                  <HiPrinter className="h-3.5 w-3.5" />
                  Waybill
                </a>
              </div>
              <WaybillsToggle order={order} />
            </div>
          );
        })}
      </div>

      {/* ── Desktop table ── */}
      <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-50 bg-gray-50/60">
              {["Reference", "Route", "Package", "Collection Date", "Status", ""].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide first:rounded-tl-2xl last:rounded-tr-2xl">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {orders.map((order, i) => {
              const ref       = order.reference_number ?? order.id ?? `ORD-${i + 1}`;
              const fromTown  = order.departure_town?.name;
              const fromProv  = order.departure_province?.name;
              const toTown    = order.destination_town?.name;
              const toProv    = order.destination_province?.name;
              const fromLabel = [fromTown, fromProv].filter(Boolean).join(", ") || "—";
              const toLabel   = [toTown, toProv].filter(Boolean).join(", ") || "—";

              const waybills = order.waybills || [];
              const hasMultipleWaybills = waybills.length > 1;

              return (
                <DesktopOrderRow key={order.id ?? i} order={order} ref_={ref} fromLabel={fromLabel} toLabel={toLabel} displayStatus={displayStatus} navigate={navigate} waybills={waybills} hasMultipleWaybills={hasMultipleWaybills} />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OrdersPage() {
  const navigate = useNavigate();
  const { selectedBusiness: business } = useSelectedBusiness();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [search, setSearch]     = useState("");
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const raw = await ParcelOrderService.index(business?.id);
        setUpcoming(raw?.upcoming_parcels ?? []);
        setPast(raw?.past_parcels ?? []);
      } catch {
        setError("Could not load orders.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [business?.id]);

  const allOrders = [...upcoming, ...past];

  const counts = {
    all:       allOrders.length,
    active:    allOrders.filter((o) => ["enroute", "collected"].includes(orderStatus(o))).length,
    // pending only counts from upcoming — past parcels are never "pending" regardless of status
    pending:   upcoming.filter((o) => orderStatus(o) === "pending").length,
    delivered: allOrders.filter((o) => ["delivered", "completed"].includes(orderStatus(o))).length,
    cancelled: allOrders.filter((o) => orderStatus(o) === "cancelled").length,
  };

  const successRate = counts.all > 0 ? Math.round((counts.delivered / counts.all) * 100) : 0;

  function applyFilters(list, isPast = false) {
    const tabDef = TABS.find((t) => t.key === activeTab);
    return list
      .filter((o) => {
        if (!tabDef?.match) return true;
        // Past parcels are never counted as pending regardless of their status
        if (isPast && tabDef.key === "pending") return false;
        return tabDef.match(orderStatus(o));
      })
      .filter((o) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (o.reference_number ?? "").toLowerCase().includes(q) ||
          (o.departure_province?.name ?? "").toLowerCase().includes(q) ||
          (o.destination_province?.name ?? "").toLowerCase().includes(q) ||
          (o.departure_town?.name ?? "").toLowerCase().includes(q) ||
          (o.destination_town?.name ?? "").toLowerCase().includes(q)
        );
      });
  }

  const filteredUpcoming = applyFilters(upcoming, false);
  const filteredPast     = applyFilters(past, true);

  if (loading) {
    return (
      <div className="px-4 py-8 lg:px-8 space-y-4">
        <Skeleton className="h-8 w-48 mb-2" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 mb-2" />)}
      </div>
    );
  }

  return (
    <div className="px-4 py-8 lg:px-8">

      {/* Page heading */}
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900">
          {business?.name ? `${business.name} — Orders` : "Orders"}
        </h1>
        <p className="text-sm text-gray-400 mt-0.5">All your shipments in one place</p>
      </div>

      {error && (
        <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
      )}

      {/* ── Stats strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
        {[
          {
            label: "Total Orders",
            value: counts.all,
            sub: "All time",
            iconCls: "bg-violet-50",
            icon: <HiOutlineCube className="h-5 w-5 text-violet-500" />,
          },
          {
            label: "Active",
            value: counts.active,
            sub: "In transit & collected",
            iconCls: "bg-cyan-50",
            icon: <HiTruck className="h-5 w-5 text-cyan-500" />,
          },
          {
            label: "Delivered",
            value: counts.delivered,
            sub: counts.all > 0 ? `${successRate}% success rate` : "—",
            iconCls: "bg-green-50",
            icon: <HiCheckCircle className="h-5 w-5 text-green-500" />,
          },
          {
            label: "Pending",
            value: counts.pending,
            sub: "Awaiting pickup",
            iconCls: "bg-amber-50",
            icon: <HiClock className="h-5 w-5 text-amber-500" />,
          },
        ].map(({ label, value, sub, iconCls, icon }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className={`rounded-xl p-2.5 flex-shrink-0 ${iconCls}`}>{icon}</div>
            <div className="min-w-0">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
              <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
              <p className="text-xs text-gray-400 truncate">{sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 mb-4 -mx-1 px-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
              activeTab === tab.key
                ? "bg-violet-600 text-white shadow-sm"
                : "bg-white border border-gray-100 text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            <span className={`text-xs rounded-full px-2 py-0.5 font-semibold ${
              activeTab === tab.key ? "bg-violet-500 text-white" : "bg-gray-100 text-gray-500"
            }`}>
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* ── Search ── */}
      <div className="relative mb-5">
        <HiMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by reference, city, or province…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 shadow-sm"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <HiXMark className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ── Orders list ── */}
      {allOrders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-20 text-center">
          <div className="h-14 w-14 rounded-2xl bg-violet-50 flex items-center justify-center mb-4">
            <HiOutlineCube className="h-7 w-7 text-violet-400" />
          </div>
          <p className="text-gray-700 font-semibold mb-1">No orders yet</p>
          <p className="text-gray-400 text-sm mb-5 max-w-xs">
            Create your first order and it will appear here — track every shipment in real time.
          </p>
          <button
            onClick={() => navigate("/business/dashboard/create")}
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
          >
            <HiPlus className="h-4 w-4" />
            Create Your First Order
          </button>
        </div>
      ) : filteredUpcoming.length === 0 && filteredPast.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 text-center">
          <p className="text-gray-400 text-sm">No orders match your search.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <OrderSection
            title="Upcoming Orders"
            orders={filteredUpcoming}
            total={upcoming.length}
            navigate={navigate}
            accent="violet"
            isPast={false}
          />
          <OrderSection
            title="Past Orders"
            orders={filteredPast}
            total={past.length}
            navigate={navigate}
            accent="gray"
            isPast={true}
          />
        </div>
      )}
    </div>
  );
}
